import React, { useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Input,
  Modal,
  Radio,
  Steps,
  Upload,
  Typography,
  Space,
  Select,
  Descriptions,
  Progress,
  Table,
} from "antd";
import { UsergroupAddOutlined, InboxOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import {
  previewImportContacts,
  importAllContacts,
} from "../../redux/actions/contactsAction";

const { Text, Paragraph } = Typography;
const { Step } = Steps;
const { Dragger } = Upload;

const REQUIRED_FIELDS = [
  { key: "firstName", label: "First Name (required)" },
  { key: "emailAddress", label: "Email Address (required)" },
];

const OPTIONAL_FIELDS = [
  { key: "lastName", label: "Last Name (optional)" },
  { key: "phoneNumber", label: "Phone Number (optional)" },
  { key: "designation", label: "Designation (optional)" },
  { key: "companyName", label: "Company Name (optional)" },
  { key: "location", label: "Location (optional)" },
  { key: "tags", label: "Tags (optional)" },
  { key: "remarks", label: "Remarks (optional)" },
];

// Mirrors CONSENT_PURPOSES in mailinflow-apis lib/consent/constants.js.
const CONSENT_PURPOSES = [
  { value: "marketing_email", label: "Marketing email" },
  { value: "transactional", label: "Transactional email only" },
  { value: "whatsapp_alerts", label: "WhatsApp alerts" },
  { value: "whatsapp_marketing", label: "WhatsApp marketing" },
];

const CONSENT_SOURCES = [
  "Signup form on our website",
  "Mailchimp audience export",
  "Another email platform",
  "In person or on paper",
  "Existing customers",
];

const EMPTY_DECLARATION = {
  mode: "declare",
  purpose: "marketing_email",
  source: "",
  obtainedAt: null,
  notes: "",
  confirmed: false,
};

export default function ImportContacts({ fetchRecords }) {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [file, setFile] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [previewInfo, setPreviewInfo] = useState(null);
  const [mapping, setMapping] = useState({});
  const [declaration, setDeclaration] = useState(EMPTY_DECLARATION);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState(null);
  const [loading, setLoading] = useState(false);

  const requiredFields = REQUIRED_FIELDS;

  const resetState = () => {
    setCurrentStep(0);
    setFile(null);
    setFileContent(null);
    setPreviewInfo(null);
    setMapping({});
    setDeclaration(EMPTY_DECLARATION);
    setImportResult(null);
    setImportError(null);
    setLoading(false);
  };

  const updateDeclaration = (patch) =>
    setDeclaration((prev) => ({ ...prev, ...patch }));

  // The API (POST /contacts/import/all) treats a missing consentDeclaration
  // as "no consent basis": every new contact is tagged consent-undeclared
  // and left out of marketing sends. Before this step existed the wizard
  // never sent one, so every imported list was unmailable by default.
  const isDeclaring = declaration.mode === "declare";
  const declarationReady = isDeclaring
    ? Boolean(declaration.source.trim()) && declaration.confirmed
    : true;

  const buildConsentDeclaration = () => {
    if (!isDeclaring) return null;
    return {
      purpose: declaration.purpose,
      source: declaration.source.trim(),
      obtainedAt: declaration.obtainedAt
        ? declaration.obtainedAt.toISOString()
        : null,
      notes: declaration.notes.trim() || null,
    };
  };

  const showModal = () => {
    resetState();
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    if (loading && currentStep === 3 && !importResult && !importError) {
      return;
    }
    setIsModalOpen(false);
  };

  const readFileAsDataUrl = (fileObj) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(e);
      reader.readAsDataURL(fileObj);
    });
  };

  const handleNextFromStep0 = async () => {
    if (!file) {
      return;
    }
    try {
      setLoading(true);
      const content = await readFileAsDataUrl(file);
      setFileContent(content);
      const result = await dispatch(
        previewImportContacts({
          fileName: file.name,
          fileContent: content,
        }),
      );
      setPreviewInfo(result);

      const autoMapping = {};
      const lowerColumns = result.columns.map((c) => c.toLowerCase());
      const tryMap = (fieldKey, candidates) => {
        for (const c of candidates) {
          const idx = lowerColumns.indexOf(c.toLowerCase());
          if (idx !== -1) {
            autoMapping[fieldKey] = result.columns[idx];
            return;
          }
        }
      };

      tryMap("firstName", ["first name", "firstname", "first"]);
      tryMap("lastName", ["last name", "lastname", "last"]);
      tryMap("emailAddress", ["email address", "email", "emailaddress"]);
      tryMap("phoneNumber", ["phone number", "phonenumber", "phone", "mobile"]);
      tryMap("designation", ["designation", "job title", "jobtitle", "title"]);
      tryMap("companyName", [
        "company name",
        "companyname",
        "company",
        "organization",
      ]);
      tryMap("location", ["location", "city", "address", "region"]);
      tryMap("tags", ["tags", "labels"]);
      tryMap("remarks", ["remarks", "notes", "note"]);

      setMapping(autoMapping);
      setCurrentStep(1);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleNextFromStep1 = () => {
    if (!previewInfo) return;
    for (const field of requiredFields) {
      if (!mapping[field.key]) {
        return;
      }
    }
    setCurrentStep(2);
  };

  const handleNextFromStep2 = () => {
    if (!declarationReady) return;
    setCurrentStep(3);
    handleStartImport();
  };

  const handleStartImport = async () => {
    if (!file || !fileContent || !previewInfo) return;
    try {
      setLoading(true);
      setImportError(null);
      const consentDeclaration = buildConsentDeclaration();
      const result = await dispatch(
        importAllContacts({
          fileName: file.name,
          fileContent,
          mapping: {
            firstName: mapping.firstName,
            lastName: mapping.lastName || null,
            emailAddress: mapping.emailAddress,
            phoneNumber: mapping.phoneNumber || null,
            designation: mapping.designation || null,
            companyName: mapping.companyName || null,
            location: mapping.location || null,
            tags: mapping.tags || null,
            remarks: mapping.remarks || null,
          },
          ...(consentDeclaration ? { consentDeclaration } : {}),
        }),
      );
      setImportResult(result);
      if (fetchRecords) {
        fetchRecords();
      }
    } catch (e) {
      console.error(e);
      setImportError(
        e?.response?.data?.message ||
          e?.message ||
          "Import failed. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      title: "Upload file",
      content: (
        <>
          <Paragraph>
            <Text strong>Step 1:</Text> Upload a CSV or Excel file. Contacts
            that don't exist yet will be created, and contacts that already
            exist (matched by email) will have the file's tags added to them.
          </Paragraph>
          <Paragraph>
            <a href="/contacts-import-template.csv" download>
              Download contacts import template
            </a>
          </Paragraph>
          <Dragger
            multiple={false}
            accept=".csv,.xlsx,.xls"
            beforeUpload={(f) => {
              setFile(f);
              return false;
            }}
            fileList={file ? [file] : []}
            onRemove={() => setFile(null)}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag file to this area to upload
            </p>
            <p className="ant-upload-hint">
              Supported formats: CSV, XLSX. Maximum contacts per import: 100 for
              free plan, 5000 for paid plan.
            </p>
          </Dragger>
        </>
      ),
    },
    {
      title: "Map columns",
      content: previewInfo && (
        <>
          <Paragraph>
            <Text strong>Step 2:</Text> Map your file columns to contact
            fields. First name and email are required. If you provide a phone
            number it should be a valid phone, and tags should be separated
            by a semicolon and space, for example:{" "}
            <code>vip; newsletter; trial</code>. Tags are added to existing
            contacts without removing their current tags.
          </Paragraph>
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Rows in file">
              {previewInfo.rowCount}
            </Descriptions.Item>
            <Descriptions.Item label="Plan">
              {previewInfo.plan}
            </Descriptions.Item>
            <Descriptions.Item label="Max per import">
              {previewInfo.maxPerImport}
            </Descriptions.Item>
          </Descriptions>
          <div style={{ marginTop: 16 }}>
            <Text strong>Required fields</Text>
            <div style={{ marginTop: 8 }}>
              {requiredFields.map((field) => (
                <div
                  key={field.key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 8,
                  }}
                >
                  <div style={{ width: 220 }}>{field.label}</div>
                  <Select
                    style={{ flex: 1 }}
                    placeholder="Select column"
                    value={mapping[field.key]}
                    onChange={(value) =>
                      setMapping((prev) => ({ ...prev, [field.key]: value }))
                    }
                  >
                    {previewInfo.columns.map((col) => (
                      <Select.Option key={col} value={col}>
                        {col}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 16 }}>
              <Text strong>Optional fields</Text>
              <div style={{ marginTop: 8 }}>
                {OPTIONAL_FIELDS.map((field) => (
                  <div
                    key={field.key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 8,
                    }}
                  >
                    <div style={{ width: 220 }}>{field.label}</div>
                    <Select
                      style={{ flex: 1 }}
                      allowClear
                      placeholder="Select column (or leave blank)"
                      value={mapping[field.key]}
                      onChange={(value) =>
                        setMapping((prev) => ({ ...prev, [field.key]: value }))
                      }
                    >
                      {previewInfo.columns.map((col) => (
                        <Select.Option key={col} value={col}>
                          {col}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      ),
    },
    {
      title: "Consent",
      content: (
        <>
          <Paragraph>
            <Text strong>Step 3:</Text> Say where these people agreed to hear
            from you. The declaration is recorded against each contact in the
            consent ledger. Contacts imported without one are tagged{" "}
            <code>consent-undeclared</code> and left out of marketing sends
            until you send them the legacy notice.
          </Paragraph>
          <Radio.Group
            value={declaration.mode}
            onChange={(e) => updateDeclaration({ mode: e.target.value })}
            style={{ display: "flex", flexDirection: "column", gap: 8 }}
          >
            <Radio value="declare">
              These contacts gave consent. Record it now.
            </Radio>
            <Radio value="none">
              Import without a consent declaration (tagged consent-undeclared,
              not mailable).
            </Radio>
          </Radio.Group>
          {isDeclaring && (
            <div
              style={{
                marginTop: 16,
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <div>
                <Text strong>Purpose</Text>
                <Select
                  style={{ width: "100%", marginTop: 4 }}
                  value={declaration.purpose}
                  onChange={(value) => updateDeclaration({ purpose: value })}
                  options={CONSENT_PURPOSES}
                />
              </div>
              <div>
                <Text strong>Where consent was collected</Text>
                <Select
                  style={{ width: "100%", marginTop: 4 }}
                  placeholder="Pick one or type your own"
                  showSearch
                  allowClear
                  value={declaration.source || undefined}
                  onChange={(value) =>
                    updateDeclaration({ source: value || "" })
                  }
                  onSearch={(value) => {
                    if (value) updateDeclaration({ source: value });
                  }}
                  options={CONSENT_SOURCES.map((s) => ({ value: s, label: s }))}
                />
              </div>
              <div>
                <Text strong>When consent was given (optional)</Text>
                <div style={{ marginTop: 4 }}>
                  <DatePicker
                    style={{ width: "100%" }}
                    value={declaration.obtainedAt}
                    onChange={(value) =>
                      updateDeclaration({ obtainedAt: value || null })
                    }
                    disabledDate={(d) => d && d.valueOf() > Date.now()}
                  />
                </div>
                <Text type="secondary">
                  One date for every row. Leave blank to record the import date.
                </Text>
              </div>
              <div>
                <Text strong>Notes (optional)</Text>
                <Input.TextArea
                  rows={2}
                  style={{ marginTop: 4 }}
                  placeholder='For example: Mailchimp audience "Patients", newsletter box ticked on signup'
                  value={declaration.notes}
                  onChange={(e) => updateDeclaration({ notes: e.target.value })}
                />
              </div>
              <Checkbox
                checked={declaration.confirmed}
                onChange={(e) =>
                  updateDeclaration({ confirmed: e.target.checked })
                }
              >
                I confirm these {previewInfo?.rowCount ?? ""} people agreed to
                receive this kind of message from us.
              </Checkbox>
            </div>
          )}
        </>
      ),
    },
    {
      title: "Import",
      content: (
        <>
          {!importResult && !importError && (
            <>
              <Paragraph>
                <Text strong>Step 4:</Text> Importing contacts. Please wait...
              </Paragraph>
              <Progress percent={loading ? 70 : 100} status="active" />
            </>
          )}
          {importError && (
            <>
              <Alert
                type="error"
                showIcon
                message="Import failed"
                description={importError}
                style={{ marginBottom: 16 }}
              />
              <Button onClick={handleStartImport}>Retry import</Button>
            </>
          )}
          {importResult && (
            <>
              <Paragraph>
                <Text strong>Import summary</Text>
              </Paragraph>
              <Descriptions bordered size="small" column={1}>
                <Descriptions.Item label="Rows in file">
                  {importResult.totalRows}
                </Descriptions.Item>
                <Descriptions.Item label="New contacts created">
                  {importResult.importedCount}
                </Descriptions.Item>
                <Descriptions.Item label="Existing contacts tagged">
                  {importResult.updatedCount}
                </Descriptions.Item>
                <Descriptions.Item label="Failed">
                  {importResult.failedCount}
                </Descriptions.Item>
                <Descriptions.Item label="Consent">
                  {isDeclaring
                    ? `Declared · ${
                        CONSENT_PURPOSES.find(
                          (p) => p.value === declaration.purpose,
                        )?.label || declaration.purpose
                      } · ${declaration.source.trim()}`
                    : "Not declared · new contacts are tagged consent-undeclared"}
                </Descriptions.Item>
              </Descriptions>
              {importResult.failedCount > 0 && (
                <div style={{ marginTop: 16 }}>
                  <Paragraph>
                    Some rows could not be imported. You can download the failed
                    rows as a CSV file, fix them, and re-upload.
                  </Paragraph>
                  <Space style={{ marginBottom: 8 }}>
                    <Button onClick={() => downloadFailedRows(importResult)}>
                      Download failed rows (CSV)
                    </Button>
                  </Space>
                  <Table
                    size="small"
                    rowKey={(record) => record.rowNumber}
                    dataSource={importResult.failedRows}
                    pagination={{ pageSize: 5 }}
                    columns={[
                      {
                        title: "Row",
                        dataIndex: "rowNumber",
                        key: "rowNumber",
                      },
                      {
                        title: "Error",
                        dataIndex: "error",
                        key: "error",
                      },
                    ]}
                  />
                </div>
              )}
            </>
          )}
        </>
      ),
    },
  ];

  const downloadFailedRows = (result) => {
    if (!result || !result.failedRows || result.failedRows.length === 0) {
      return;
    }
    const allKeys = new Set();
    result.failedRows.forEach((fr) => {
      Object.keys(fr.row || {}).forEach((k) => allKeys.add(k));
    });
    const headers = Array.from(allKeys);
    const lines = [];
    lines.push(["RowNumber", "Error", ...headers].join(","));
    result.failedRows.forEach((fr) => {
      const values = headers.map((h) => {
        const value = fr.row && fr.row[h] != null ? String(fr.row[h]) : "";
        const escaped = value.replace(/"/g, '""');
        if (escaped.search(/("|,|\n)/g) >= 0) {
          return `"${escaped}"`;
        }
        return escaped;
      });
      const errorEscaped = fr.error.replace(/"/g, '""');
      const rowNumberStr = String(fr.rowNumber);
      lines.push(
        [
          rowNumberStr,
          errorEscaped.search(/("|,|\n)/g) >= 0
            ? `"${errorEscaped}"`
            : errorEscaped,
          ...values,
        ].join(","),
      );
    });
    const blob = new Blob([lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "contacts-import-failed-rows.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const footerButtons = [];
  if (currentStep === 0) {
    footerButtons.push(
      <Button key="cancel" onClick={handleCancel}>
        Cancel
      </Button>,
    );
    footerButtons.push(
      <Button
        key="next"
        type="primary"
        onClick={handleNextFromStep0}
        disabled={!file}
        loading={loading}
      >
        Next
      </Button>,
    );
  } else if (currentStep === 1) {
    footerButtons.push(
      <Button key="back" onClick={() => setCurrentStep(0)} disabled={loading}>
        Back
      </Button>,
    );
    footerButtons.push(
      <Button key="cancel" onClick={handleCancel} disabled={loading}>
        Cancel
      </Button>,
    );
    const missingRequired = requiredFields.some((f) => !mapping[f.key]);
    footerButtons.push(
      <Button
        key="next"
        type="primary"
        onClick={handleNextFromStep1}
        disabled={missingRequired}
        loading={loading}
      >
        Next
      </Button>,
    );
  } else if (currentStep === 2) {
    footerButtons.push(
      <Button key="back" onClick={() => setCurrentStep(1)} disabled={loading}>
        Back
      </Button>,
    );
    footerButtons.push(
      <Button key="cancel" onClick={handleCancel} disabled={loading}>
        Cancel
      </Button>,
    );
    footerButtons.push(
      <Button
        key="next"
        type="primary"
        onClick={handleNextFromStep2}
        disabled={!declarationReady}
        loading={loading}
      >
        Start import
      </Button>,
    );
  } else if (currentStep === 3) {
    if (!importResult && !importError) {
      footerButtons.push(
        <Button key="close" type="primary" disabled>
          Close
        </Button>,
      );
    } else {
      footerButtons.push(
        <Button
          key="close"
          type="primary"
          onClick={() => {
            setIsModalOpen(false);
          }}
        >
          Close
        </Button>,
      );
    }
  }

  return (
    <>
      <Button
        icon={<UsergroupAddOutlined />}
        onClick={showModal}
        title="Import Contacts"
      >
        Import
      </Button>
      {isModalOpen && (
        <Modal
          title="Import contacts"
          open={isModalOpen}
          footer={footerButtons}
          onCancel={handleCancel}
          closable={false}
          maskClosable={false}
          destroyOnClose
          width="min(720px, 90vw)"
        >
          <Steps
            current={currentStep}
            size="small"
            style={{ marginBottom: 24 }}
          >
            {steps.map((item) => (
              <Step key={item.title} title={item.title} />
            ))}
          </Steps>
          <div>{steps[currentStep].content}</div>
        </Modal>
      )}
    </>
  );
}
